import { api } from '@/lib/api';
import { type AdminUser } from '@/types/user';

// DTOs based on backend requirements
export interface UserCreateDTO {
  name?: string;
  phone_number?: string;
  email?: string;
  role: string;
  organization_id?: number;
  is_active?: boolean;
}

export interface UserUpdateDTO {
  name: string;
  email: string;
  phone: string;
  role: string;
  organization_id: number | null;
  is_active: boolean;
}

export interface UserRegistrationResponseDTO {
  user_id: string;
  sync_status: 'synced' | 'pending' | 'failed';
  message?: string;
}

export interface AdminListDTO {
  admins: AdminUser[];
  total_count: number;
  active_count: number;
}

export interface UserUpdateStatusDTO {
  user_id: string;
  is_active: boolean;
}

// User API service layer
export const adminUserApi = {
  // List users with optional search query
  getUsers: async (query?: string): Promise<AdminListDTO> => {
    const params = query ? { query } : undefined;
    return api.get<AdminListDTO>('/admin/users', {
      ...(params && {
        headers: {
          'X-Query-Params': new URLSearchParams(params).toString(),
        },
      }),
    });
  },

  // Create a new user
  createUser: async (
    data: UserCreateDTO
  ): Promise<UserRegistrationResponseDTO> => {
    return api.post<UserRegistrationResponseDTO>('/admin/register', data);
  },

  // Update user status (activate/deactivate)
  updateUserStatus: async (data: UserUpdateStatusDTO): Promise<AdminUser> => {
    return api.put<AdminUser>('/admin/status', data);
  },

  // Get single user details (when endpoint becomes available)
  getUser: async (userId: string): Promise<AdminUser> => {
    return api.get<AdminUser>(`/admin/users/${userId}`);
  },

  // Update user details
  updateUser: async (
    userId: string,
    data: UserUpdateDTO
  ): Promise<AdminUser> => {
    return api.put<AdminUser>(`/admin/update/${userId}`, data);
  },

  // Delete user (when endpoint becomes available)
  deleteUser: async (userId: string): Promise<void> => {
    return api.delete<void>(`/admin/users/${userId}`);
  },

  // Get role permissions mapping
  getRolePermissions: async (): Promise<Record<string, string[]>> => {
    return api.get<Record<string, string[]>>('/admin/role-permissions');
  },
};
